import DateHelper from '../../components/Helpers/DateHelper';
import HttpHelper from '../../components/Helpers/HttpHelper';
import { ErrorLogService } from '../ErrorLogService';
import ApiServiceBase from './ApiServiceBase';

const adminApiRouteBase = 'api/admin';

export default class AdminApiService extends ApiServiceBase {
  private static className = 'AdminApiService';

  public static async impersonateUser(userId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/impersonate/${userId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - impersonateUser()`, `An error occurred while impersonating user: ${userId}.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async stopImpersonatingUser(): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/stop-impersonation`));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - stopImpersonatingUser()`, `An error occurred while stopping impersonation.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAllComplianceDocuments(): Promise<Admin_ComplianceDocument_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_ComplianceDocument_ViewModel[]>(() => HttpHelper.get(`api/files`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getAllComplianceDocuments()`,
        'An error occurred while loading compliance documents for admins.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async updateComplianceDocument(documentId: string, propertyName: string, isEnabled: boolean): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.put(`api/files/compliance-document/${documentId}/${propertyName}/${isEnabled}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - updateComplianceDocument()`,
        'An error occurred while updating a compliance document.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAllReviews(): Promise<Reviews_Review_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Reviews_Review_ViewModel[]>(() => HttpHelper.get(`${adminApiRouteBase}/reviews`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getAllReviews()`, 'An error occurred while loading reviews for admins.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteReview(reviewId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/reviews/${reviewId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteReview()`,
        `An error occurred while deleting the review with ID: ${reviewId}`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async regenerateReviewsSummary(userId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/users/${userId}/regenerate-reviews-summary`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - regenerateReviewsSummary()`,
        `An error occurred while regenerating reviews summary for user ID: ${userId}`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAllUsers(): Promise<User_BasicDetails_ViewModel[]> {
    try {
      const result = await this.makeApiCall<User_BasicDetails_ViewModel[]>(() => HttpHelper.get(`${adminApiRouteBase}/users`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getAllUsers()`, `An error occurred while getting all users.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getBasicUserInfoById(userId: string): Promise<User_BasicDetails_ViewModel> {
    try {
      const result = await this.makeApiCall<User_BasicDetails_ViewModel>(() =>
        HttpHelper.get(`${adminApiRouteBase}/users/basic/${userId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getBasicUserInfoById()`,
        `An error occurred while getting user info for ID: '${userId}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async searchUsers(searchText: string, userRole?: string): Promise<User_BasicDetails_ViewModel[]> {
    try {
      const result = await this.makeApiCall<User_BasicDetails_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/users/search/${searchText}${userRole ? '/' + userRole : ''}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - searchUsers()`,
        `An error occurred while searching users with search text: '${searchText}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async searchThePlanDetails(searchText: string): Promise<Admin_ThePlanDetail_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanDetail_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/the-plan/search/${searchText}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - searchThePlanDetails()`,
        `An error occurred while searching the Plan details with search text: '${searchText}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async searchTheOngoingDetails(searchText: string): Promise<Admin_TheOngoingDetail_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingDetail_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/the-ongoing/search/${searchText}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - searchTheOngoingDetails()`,
        `An error occurred while searching the Ongoing details with search text: '${searchText}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getUserById(userId: string): Promise<Admin_UserDetails_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_UserDetails_ViewModel>(() => HttpHelper.get(`${adminApiRouteBase}/users/${userId}`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getUserById()`, `An error occurred while getting info for user: ${userId}.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async updateUser(editModel: Admin_EditUser_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.put(`${adminApiRouteBase}/users/${editModel.id}`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - updateUser()`, `An error occurred while updating the user: ${editModel.id}.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteUser(userIdToDelete: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/users/${userIdToDelete}`));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - deleteUser()`, `An error occurred while deleting user '${userIdToDelete}'.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAllUserRoles(): Promise<Admin_UserRole_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_UserRole_ViewModel[]>(() => HttpHelper.get(`${adminApiRouteBase}/roles`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getAllUserRoles()`, `An error occurred while getting all user roles.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async updateUserRole(editModel: Admin_UserRole_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.put(`${adminApiRouteBase}/roles`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - updateUserRole()`, `An error occurred while updating a user role.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteUserRole(roleIdToDelete: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/roles/${roleIdToDelete}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteUserRole()`,
        `An error occurred while deleting user role '${roleIdToDelete}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async createUserRole(newRoleName: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/roles`, newRoleName));
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - createUserRole()`, `An error occurred while creating a user role.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getUserRoleById(roleId: string): Promise<Admin_UserRole_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_UserRole_ViewModel>(() => HttpHelper.get(`${adminApiRouteBase}/roles/${roleId}`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getUserRoleById()`,
        `An error occurred while getting user role by ID ${roleId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getUsersInRole(role: string): Promise<User_BasicDetails_ViewModel[]> {
    try {
      const result = await this.makeApiCall<User_BasicDetails_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/users/roles/${role}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getUsersInRole()`, `An error occurred while getting users in role: ${role}`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getRolesForUser(userId: string): Promise<string[]> {
    try {
      const result = await this.makeApiCall<string[]>(() => HttpHelper.get(`${adminApiRouteBase}/users/${userId}/roles`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getRolesForUser()`,
        `An error occurred while getting roles for user: ${userId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async addRoleToUser(userId: string, roleName: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/users/${userId}/roles`, roleName));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - addRoleToUser()`,
        `An error occurred while adding a role '${roleName}' to the user: ${userId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteRoleFromUser(userId: string, role: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/users/${userId}/roles`, role));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteRoleFromUser()`,
        `An error occurred while deleting a role (${role}) from the user: ${userId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getOrphanIntakeResponses(): Promise<Admin_IntakeResponse_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_IntakeResponse_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/orphan-intake-responses`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getOrphanIntakeResponses()`,
        `An error occurred while getting orphan intake forms.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getRecentIntakeResponses(): Promise<Admin_IntakeResponse_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_IntakeResponse_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/recent-intake-responses`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getRecentIntakeResponses()`,
        `An error occurred while getting recent intake responses.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getIntakeResponse(intakeResponseId: string): Promise<Admin_IntakeResponse_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_IntakeResponse_ViewModel>(() =>
        HttpHelper.get(`${adminApiRouteBase}/intake-response/${intakeResponseId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getIntakeResponse()`,
        `An error occurred while getting intake response '${intakeResponseId}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editIntakeResponse(editModel: Admin_IntakeResponse_EditModel): Promise<void> {
    try {
      const result = await this.makeApiCall<void>(() => HttpHelper.post(`${adminApiRouteBase}/edit-intake-response`, editModel));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editIntakeResponse()`,
        `An error occurred while editing intake response '${editModel.id}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async searchEvents(searchText: string): Promise<Admin_Event_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_Event_ViewModel[]>(() =>
        HttpHelper.get(`${adminApiRouteBase}/search-events/${searchText}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - searchEvents()`, `An error occurred while searching all events.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getEventById(eventId: string): Promise<Admin_Event_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_Event_ViewModel>(() => HttpHelper.get(`${adminApiRouteBase}/events/${eventId}`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getEventById()`, `An error occurred while getting event for ID: ${eventId}.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async updateEvent(editModel: Admin_Event_EditModel): Promise<void> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.put(`${adminApiRouteBase}/events/${editModel.id}`, editModel));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getEventById()`,
        `An error occurred while updating event for ID: ${editModel.id}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteEventById(eventId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/events/${eventId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteEventById()`,
        `An error occurred while deleting event for ID: ${eventId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getRecentUserAgreements(): Promise<User_UserAgreement_ViewModel[]> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.get<User_UserAgreement_ViewModel[]>(`api/useragreements/recent`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getRecentUserAgreements()`,
        `An error occurred while getting recent user agreements.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getRecording(recordingId: string): Promise<Admin_MeetingRecording_ViewModel> {
    try {
      const result = await this.makeApiCall(() =>
        HttpHelper.get<Admin_MeetingRecording_ViewModel>(`${adminApiRouteBase}/recordings/${recordingId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getAllRecordings()`,
        `An error occurred while getting recording for ID ${recordingId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getOrphanRecordings(): Promise<Admin_MeetingRecording_ViewModel[]> {
    try {
      const result = await this.makeApiCall(() =>
        HttpHelper.get<Admin_MeetingRecording_ViewModel[]>(`${adminApiRouteBase}/orphan-recordings`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getOrphanRecordings()`, `An error occurred while getting orphan recordings.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async searchRecordings(searchText: string): Promise<Admin_MeetingRecording_ViewModel[]> {
    try {
      const result = await this.makeApiCall(() =>
        HttpHelper.get<Admin_MeetingRecording_ViewModel[]>(`${adminApiRouteBase}/search-recordings/${searchText}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - searchRecordings()`, `An error occurred while searching recordings.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editRecording(editModel: Admin_EditMeetingRecording_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.put(`${adminApiRouteBase}/recordings/${editModel.id}`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editRecording()`,
        `An error occurred while editing a recording with ID: ${editModel.id}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteZoomRecording(recordingId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/zoom/recording/${recordingId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteZoomRecording()`,
        `An error occurred while deleting recording with ID: ${recordingId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async search(searchText: string): Promise<Admin_Search_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_Search_ViewModel>(() =>
        HttpHelper.get(`/api/admin/search?q=${encodeURIComponent(searchText)}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - search()`, 'An error occurred while running the admin search.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getFeatureFlags(): Promise<Admin_FeatureFlag_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_FeatureFlag_ViewModel[]>(() => HttpHelper.get(`/api/admin/feature-flags`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getFeatureFlags()`, 'An error occurred while getting admin feature flags data.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async setFeatureFlags(editModel: Admin_FeatureFlag_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`/api/admin/set-feature-flag`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - setFeatureFlags()`,
        `An error occurred while setting the admin feature flag for flag ${editModel.id}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getDashboardData(): Promise<Admin_Dashboard_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_Dashboard_ViewModel>(() => HttpHelper.get(`/api/admin/dashboard`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getDashboardData()`, 'An error occurred while getting admin dashboard data.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAdvisorsOngoingAvailability(): Promise<Admin_TheOngoingAdvisorsAvailability_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingAdvisorsAvailability_ViewModel[]>(() =>
        HttpHelper.get(`/api/admin/advisors-ongoing-availability`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getAdvisorsOngoingAvailability()`,
        'An error occurred while getting advisors Ongoing availability.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAdvisorsPlanAvailability(): Promise<Admin_ThePlanAdvisorsAvailability_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanAdvisorsAvailability_ViewModel[]>(() =>
        HttpHelper.get(`/api/admin/advisors-plan-availability`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getAdvisorsPlanAvailability()`,
        'An error occurred while getting advisors Plan availability.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getTheOngoingSignups(): Promise<Admin_TheOngoingSignup_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingSignup_ViewModel[]>(() => HttpHelper.get(`/api/admin/get-the-ongoing-signups`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getTheOngoingSignups()`, 'An error occurred while getting the ongoing signups.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getThePlanSignups(): Promise<Admin_ThePlanSignup_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanSignup_ViewModel[]>(() => HttpHelper.get(`/api/admin/get-the-plan-signups`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getThePlanSignups()`, 'An error occurred while getting the plan signups.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getTheOngoingSignup(theOngoingSignupId: string): Promise<Admin_TheOngoingSignup_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingSignup_ViewModel>(() =>
        HttpHelper.get(`/api/admin/get-the-ongoing-signup/${theOngoingSignupId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getTheOngoingSignup()`,
        `An error occurred while getting the ongoing signup for ID: ${theOngoingSignupId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getThePlanSignup(thePlanSignupId: string): Promise<Admin_ThePlanSignup_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanSignup_ViewModel>(() =>
        HttpHelper.get(`/api/admin/get-the-plan-signup/${thePlanSignupId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getThePlanSignup()`,
        `An error occurred while getting the plan signup for ID: ${thePlanSignupId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getTheOngoingInstances(): Promise<Admin_TheOngoingDetail_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingDetail_ViewModel[]>(() =>
        HttpHelper.get(`/api/admin/get-the-ongoing-instances`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getTheOngoingInstances()`,
        'An error occurred while getting the ongoing instances.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getTheOngoingInstance(theOngoingInstanceId: string): Promise<Admin_TheOngoingDetail_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_TheOngoingDetail_ViewModel>(() =>
        HttpHelper.get(`/api/admin/get-the-ongoing-instance/${theOngoingInstanceId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getTheOngoingInstance()`,
        `An error occurred while getting the ongoing instance for ID: ${theOngoingInstanceId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getThePlanInstances(): Promise<Admin_ThePlanDetail_ViewModel[]> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanDetail_ViewModel[]>(() => HttpHelper.get(`/api/admin/get-the-plan-instances`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getThePlanInstances()`, 'An error occurred while getting the plan instances.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getThePlanInstance(thePlanInstanceId: string): Promise<Admin_ThePlanDetail_ViewModel> {
    try {
      const result = await this.makeApiCall<Admin_ThePlanDetail_ViewModel>(() =>
        HttpHelper.get(`/api/admin/get-the-plan-instance/${thePlanInstanceId}`)
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getThePlanInstance()`,
        `An error occurred while getting the plan instance for ID: ${thePlanInstanceId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editTheOngoing(editModel: Admin_TheOngoingDetail_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-ongoing-instance`, editModel));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - editTheOngoing()`, 'An error occurred while adding a new Ongoing instance.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editThePlan(editModel: Admin_ThePlanDetail_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-plan-instance`, editModel));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - editThePlan()`, 'An error occurred while adding a new Plan instance.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editTheOngoingInvoice(editModel: Admin_Invoice_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/edit-the-ongoing-invoice`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editTheOngoingInvoice()`,
        'An error occurred while editing an Ongoing instance invoice.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editThePlanInvoice(editModel: Admin_Invoice_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/edit-the-plan-invoice`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editThePlanInvoice()`,
        'An error occurred while editing a Plan instance invoice.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editTheOngoingSignup(editModel: Admin_TheOngoingSignup_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-ongoing-signup`, editModel));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - editTheOngoingSignup()`, 'An error occurred while editing an ongoing signup.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editThePlanSignup(editModel: Admin_ThePlanSignup_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-plan-signup`, editModel));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - editThePlanSignup()`, 'An error occurred while editing a Plan signup.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editThePlanSignupStatus(editModel: Admin_ThePlanSignup_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-plan-signup-status`, editModel));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editThePlanSignupStatus()`,
        'An error occurred while editing a Plan signup status.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async editThePlanTargetFollowupDate(editModel: Admin_ThePlanSignup_EditModel): Promise<string> {
    try {
      const result = await this.makeApiCall(() =>
        HttpHelper.post<string>(`${adminApiRouteBase}/edit-the-plan-signup-target-date`, editModel)
      );
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - editThePlanTargetFollowupDate()`,
        'An error occurred while editing a Plan signup status.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteTheOngoing(theOngoingDetailId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete<string>(`${adminApiRouteBase}/delete-the-ongoing-instance/${theOngoingDetailId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteTheOngoing()`,
        `An error occurred while deleting the ongoing instance: ${theOngoingDetailId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteThePlan(thePlanDetailId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete<string>(`${adminApiRouteBase}/delete-the-plan-instance/${thePlanDetailId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteThePlan()`,
        `An error occurred while deleting the plan instance: ${thePlanDetailId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteTheOngoingSignup(theOngoingSignupId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete<string>(`${adminApiRouteBase}/delete-the-ongoing-signup/${theOngoingSignupId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteTheOngoingSignup()`,
        `An error occurred while deleting the ongoing signup: ${theOngoingSignupId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteThePlanSignup(thePlanSignupId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete<string>(`${adminApiRouteBase}/delete-the-plan-signup/${thePlanSignupId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteThePlanSignup()`,
        `An error occurred while deleting the plan signup: ${thePlanSignupId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteIntakeResponse(intakeResponseId: string): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.delete<string>(`${adminApiRouteBase}/delete-intake-response/${intakeResponseId}`));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteIntakeResponse()`,
        `An error occurred while deleting the intake response: ${intakeResponseId}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async refreshThePlanInvoices(): Promise<void> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/refresh-the-plan-invoices`, null));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - refreshThePlanInvoices()`,
        'An error occurred while refreshing the plan invoices.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async clearCachedServerData(): Promise<void> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.post(`${adminApiRouteBase}/clear-cached-data`, null));
      return result!;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - clearCachedServerData()`,
        'An error occurred while clearing cached server data.',
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getUserPayments(
    userType: 'advisor' | 'affiliate',
    payPeriod: Date | string
  ): Promise<Admin_UserPaymentDashboard_ViewModel> {
    try {
      const result = await this.makeApiCall(() =>
        HttpHelper.get<Admin_UserPaymentDashboard_ViewModel>(
          `${adminApiRouteBase}/${userType}payments/${payPeriod instanceof Date ? DateHelper.formatDateString(payPeriod, 'yyyy-MM-dd') : payPeriod}`
        )
      );
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getUserPayments()`, 'An error occurred while getting user payments.', error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async postNewAdvisorPayment(advisorId: string, editModel: Admin_PostNewUserPayment_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() =>
        HttpHelper.post<Admin_UserPaymentDashboard_ViewModel>(`${adminApiRouteBase}/advisorpayments/${advisorId}/logpayment`, editModel)
      );
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - postNewAdvisorPayment()`,
        `An error occurred while posting an payment for advisor ID '${advisorId}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async postNewAffiliatePayment(affiliateId: string, editModel: Admin_PostNewUserPayment_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() =>
        HttpHelper.post<Admin_UserPaymentDashboard_ViewModel>(`${adminApiRouteBase}/affiliatepayments/${affiliateId}/logpayment`, editModel)
      );
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - postNewAffiliatePayment()`,
        `An error occurred while posting an payment for affiliate ID '${affiliateId}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getSharedAdvisorAttributes(): Promise<Admin_Attribute_ViewModel[]> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.get<Admin_Attribute_ViewModel[]>(`${adminApiRouteBase}/advisorattributes`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getSharedAdvisorAttributes()`,
        `An error occurred while getting shared advisor attributes.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async deleteAdvisorAttribute(attributeIdToDelete: string): Promise<void> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.delete(`${adminApiRouteBase}/advisorattributes/${attributeIdToDelete}`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - deleteAdvisorAttribute()`,
        `An error occurred while deleting an advisor attribute.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async updateAdvisorAttribute(editModel: Admin_Attribute_EditModel): Promise<void> {
    try {
      await this.makeApiCall(() => HttpHelper.put(`${adminApiRouteBase}/advisorattributes`, editModel));
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - updateAdvisorAttribute()`,
        `An error occurred while updating an advisor attribute.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getComplianceReport(userId: string): Promise<ComplianceReport> {
    try {
      const result = await this.makeApiCall(() => HttpHelper.get<ComplianceReport>(`${adminApiRouteBase}/users/${userId}/report`));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getComplianceReport()`,
        `An error occurred while getting the compliance report for user '${userId}'.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getAllAdvisorDetails(queryStringParams?: string): Promise<Advisor_BasicDetails_ViewModel[]> {
    const url = `${adminApiRouteBase}/get-advisors-details${queryStringParams ?? ''}`;

    try {
      const result = await this.makeApiCall<Advisor_BasicDetails_ViewModel[]>(() => HttpHelper.get(url));

      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(
        `${this.className} - getAllAdvisorDetails()`,
        `An error occurred while loading advisors details: ${url}.`,
        error
      );

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getUploadedFiles(): Promise<UploadedFiles_File_ViewModel[]> {
    const url = `${adminApiRouteBase}/uploaded-files`;

    try {
      const result = await this.makeApiCall<UploadedFiles_File_ViewModel[]>(() => HttpHelper.get(url));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getUploadedFiles()`, `An error occurred while getting uploaded files.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }

  public static async getStripeInfo(stripeId: string): Promise<unknown> {
    const url = `${adminApiRouteBase}/get-stripe-info/${stripeId}`;

    try {
      const result = await this.makeApiCall<unknown>(() => HttpHelper.get(url));
      return result;
    } catch (error: unknown) {
      ErrorLogService.logError(`${this.className} - getStripeInfo()`, `An error occurred while getting stripe info.`, error);

      // Rethrow the error so the calling code can react and fail gracefully
      throw error;
    }
  }
}
